import type { ReactNode } from 'react'

import { ArrowRight01Icon, ChampionIcon } from '@hugeicons/react'

import {
  OBJECTIVES_FEATURE_COPY,
  OBJECTIVES_HERO_ART
} from '@/shared/resourceModuleParity'
import { getObjectiveResourceType } from '@/features/objectives/components/ObjectiveItem'
import { useObjectives } from '@/features/objectives/hooks/useObjectives'
import {
  getProgressData,
  progressVariant,
  selectObjectivesPreview
} from '@/features/objectives/model/progress'
import { getLadderState, type Goal, type LadderState } from '@/features/objectives/model/milestones'
import { ladderActiveMilestonePercent } from '@/features/objectives/model/milestoneCopy'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Card } from '@/shared/ui/Card'
import { SectionHeader } from '@/shared/ui/Layout'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { ResourceModuleIcon } from '@/shared/ui/ResourceModuleIcon'
import { RewardPill } from '@/shared/ui/RewardPill'
import type { ObjectiveWithProgress } from '@/types/objective'

import { BlockEmptyState } from '../blocks/BlockEmptyState'
import { BlockSkeleton } from '../blocks/BlockSkeleton'

import type { BlockComponentProps } from '../blocks/index'

/** Título do widget: até 2 linhas, sem reticências. */
const WidgetObjectiveTitle = ({ children }: { children: ReactNode }) => (
  <p className="m-0 min-w-0 flex-1 text-sm font-bold leading-snug text-gray-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden [text-overflow:clip]">
    {children}
  </p>
)

/** Formato compacto do widget — não altera os cards de `/objetivos`. */
const ladderCompactProgressLabel = (state: LadderState): string => {
  if (state.completed || state.nextTarget == null) {
    return `${state.currentCount}/${state.totalTarget}`
  }

  return `${Math.min(state.currentCount, state.nextTarget)}/${state.nextTarget}`
}

/** Linha de meta no preview da home — escada ou objetivo solto. */
const GoalRow = ({ goal }: { goal: Goal }) =>
  goal.kind === 'ladder' ? <LadderRow goal={goal} /> : <ObjectiveRow objective={goal.objective} />

const LadderRow = ({ goal }: { goal: Extract<Goal, { kind: 'ladder' }> }) => {
  const { navigateTo } = useNavigation()
  const state = getLadderState(goal.milestones)
  const sample = goal.milestones[0] ?? null
  const resourceType = getObjectiveResourceType(sample)
  const coinReward = state.completed ? state.totalCoinReward : state.nextReward ?? 0
  const percent = state.completed ? 100 : ladderActiveMilestonePercent(state)
  const variant = state.completed ? 'success' : state.currentCount > 0 ? 'progress' : 'primary'

  return (
    <button
      type="button"
      onClick={() => navigateTo('/objetivos')}
      className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-4 py-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      <ResourceModuleIcon
        resourceType={resourceType}
        size={18}
        tileSize={40}
        completed={state.completed}
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-start justify-between gap-2">
          <WidgetObjectiveTitle>{goal.group.name}</WidgetObjectiveTitle>
          <div className="flex shrink-0 items-center gap-1">
            <RewardPill type="coins" value={coinReward} compact />
            <ArrowRight01Icon size={13} className="text-gray-600" aria-hidden />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
            {ladderCompactProgressLabel(state)}
          </span>
          <ProgressBar value={percent} variant={variant} size="sm" className="flex-1" />
          <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
            {percent}%
          </span>
        </div>
      </div>
    </button>
  )
}

const ObjectiveRow = ({ objective }: { objective: ObjectiveWithProgress }) => {
  const { navigateTo } = useNavigation()
  const resourceType = getObjectiveResourceType(objective)
  const { name, coin_reward, completed } = objective
  const { sumCurrent, sumTarget, percent } = getProgressData(objective)
  const pVariant = progressVariant(completed, percent)

  return (
    <button
      type="button"
      onClick={() => navigateTo('/objetivos')}
      className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-4 py-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      <ResourceModuleIcon
        resourceType={resourceType}
        size={18}
        tileSize={40}
        completed={completed}
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-start justify-between gap-2">
          <WidgetObjectiveTitle>{name}</WidgetObjectiveTitle>
          <div className="flex shrink-0 items-center gap-1">
            <RewardPill type="coins" value={coin_reward} compact />
            <ArrowRight01Icon size={13} className="text-gray-600" aria-hidden />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
            {sumCurrent}/{sumTarget}
          </span>
          <ProgressBar
            value={completed ? 100 : percent}
            variant={pVariant}
            size="sm"
            className="flex-1"
          />
          <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
            {completed ? 100 : percent}%
          </span>
        </div>
      </div>
    </button>
  )
}

// ── ObjectivesCard ────────────────────────────────────────────
export const ObjectivesCard = ({ block }: Partial<BlockComponentProps>) => {
  const { navigateTo } = useNavigation()
  const { goals, completedCount, totalCount, overallPercent, isLoading } =
    useObjectives()

  if (isLoading) return <BlockSkeleton rows={3} />

  /*
    Early return **antes** do `<Card>`: `BlockEmptyState` traz o próprio cartão,
    e o hero deste widget desenha `{completedCount}/{totalCount}` — sem meta
    alguma ele não tem o que mostrar. Sem `action`: vazio de configuração não
    tem regra a explicar.
  */
  if (totalCount === 0)
    return (
      <BlockEmptyState
        icon={ChampionIcon}
        title="Nenhuma meta disponível"
        hint="As metas do evento aparecem aqui assim que forem publicadas."
      />
    )

  const preview = selectObjectivesPreview(goals, block?.settings?.limit ?? 3)

  return (
    <Card className="card-gamification overflow-hidden">
      {/* ── Hero ── */}
      <div
        className="relative overflow-hidden px-4 pb-5 pt-3"
        style={{ background: 'var(--gradient-hero)' }}
      >
        <img
          src={OBJECTIVES_HERO_ART}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="pointer-events-none absolute bottom-0 right-0 h-[118px] w-auto select-none object-contain opacity-95"
        />

        <SectionHeader
          tone="inverse"
          icon={
            <ResourceModuleIcon
              resourceType={null}
              size={14}
              variant="plain"
            />
          }
          title={OBJECTIVES_FEATURE_COPY.plural}
        />

        <div className="mt-3 pr-28">
          <div className="mb-2 flex items-end justify-between gap-3">
            <div className="flex items-baseline gap-1 leading-none">
              <span className="text-5xl font-extrabold leading-none text-white">
                {overallPercent}
              </span>
              <span className="text-2xl font-bold text-white/80">%</span>
              <span className="ml-1 text-xs leading-snug text-white/70">concluído</span>
            </div>

            <span className="shrink-0 text-2xs font-bold leading-none text-white">
              {completedCount}/{totalCount}
            </span>
          </div>

          <ProgressBar
            value={overallPercent}
            size="sm"
            className="[&>div]:h-2 [&>div]:bg-white/20 [&>div]:shadow-none [&>div>div]:bg-white/90"
          />
        </div>
      </div>

      {/* ── List ── */}
      {preview.length > 0 ? (
        <div className="flex flex-col divide-y divide-gray-100">
          {preview.map(goal => (
            <GoalRow key={goal.id} goal={goal} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 px-4 py-5 text-center">
          {/* `totalCount === 0` já retornou antes, no `BlockEmptyState`. */}
          <p className="m-0 text-sm font-bold text-gray-900">
            {completedCount === totalCount
              ? 'Todas as metas concluídas!'
              : 'Nenhuma meta pendente'}
          </p>
        </div>
      )}


      {/* ── CTA ── */}
      <button
        type="button"
        onClick={() => navigateTo('/objetivos')}
        className="flex w-full cursor-pointer items-center justify-center gap-1 border-0 border-t border-solid border-gray-100 bg-transparent py-3 text-2xs font-bold text-[var(--color-brand)] transition-colors hover:bg-gray-50"
      >
        Ver todas as metas
        <ArrowRight01Icon size={13} aria-hidden />
      </button>
    </Card>
  )
}
